import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  useCancelChatScheduledMutation,
  useGetChatScheduledQuery,
  useSendChatScheduledNowMutation,
  useUpdateChatScheduledMutation,
} from "@/services/(my-space)/api.chat";
import { getErrorMessage } from "@/utils/error.utils";
import type { IChatScheduledMessage } from "@/types/(my-space)/chat.interface";
import { chatErrorCode } from "../_lib/chat-utils";

/** It is going out right now — nothing to do but show the fresh list. */
const SENDING_CODE = "chat.scheduled_sending";
const NOT_FOUND_CODE = "chat.scheduled_not_found";

/**
 * My scheduled messages in one chat. The list refetches on every mutation and
 * on the socket's `scheduled_updated` (via the `Scheduled` tag in chatHub).
 */
export const useScheduledMessages = (conversationId: number) => {
  const { t } = useTranslation("chat");
  const query = useGetChatScheduledQuery(conversationId, {
    refetchOnMountOrArgChange: true,
  });
  const [sendNow] = useSendChatScheduledNowMutation();
  const [update] = useUpdateChatScheduledMutation();
  const [cancel] = useCancelChatScheduledMutation();
  /** The row a mutation is in flight for; its actions are disabled meanwhile. */
  const [busyId, setBusyId] = useState<number | null>(null);

  const items = useMemo(
    () =>
      [...(query.data ?? [])].sort(
        (a, b) => new Date(a.sendAt).getTime() - new Date(b.sendAt).getTime(),
      ),
    [query.data],
  );
  const failedCount = useMemo(
    () => items.filter((m) => m.status === "failed").length,
    [items],
  );

  const run = useCallback(
    async (item: IChatScheduledMessage, action: () => Promise<unknown>, fallbackKey: string) => {
      setBusyId(item.id);
      try {
        await action();
        return true;
      } catch (e) {
        const code = chatErrorCode(e);
        if (code === SENDING_CODE || code === NOT_FOUND_CODE) {
          toast(getErrorMessage(e, t(fallbackKey)));
          void query.refetch();
        } else {
          toast.error(getErrorMessage(e, t(fallbackKey)));
        }
        return false;
      } finally {
        setBusyId(null);
      }
    },
    [query, t],
  );

  const ref = (item: IChatScheduledMessage) => ({ id: item.id, conversationId });

  return {
    items,
    failedCount,
    isLoading: query.isLoading,
    busyId,
    sendNow: (item: IChatScheduledMessage) =>
      run(item, () => sendNow(ref(item)).unwrap(), "scheduled.errors.send_now"),
    reschedule: (item: IChatScheduledMessage, sendAt: Date) =>
      run(
        item,
        () => update({ ...ref(item), sendAt: sendAt.toISOString() }).unwrap(),
        "scheduled.errors.update",
      ),
    editText: (item: IChatScheduledMessage, body: string) =>
      run(item, () => update({ ...ref(item), body }).unwrap(), "scheduled.errors.update"),
    cancel: (item: IChatScheduledMessage) =>
      run(item, () => cancel(ref(item)).unwrap(), "scheduled.errors.cancel"),
  };
};

export type ScheduledMessagesState = ReturnType<typeof useScheduledMessages>;
